import React, { useEffect, useMemo, useState } from "react";
import type { StandbyItemAPI, StandbyMovAPI } from "./standbyData-comercial";

type ComercialStandbyAction = "baixar" | "retornar";
type ComercialStandbyScope = "item" | "os";

type ComercialStandbySidePanelProps = {
  open: boolean;
  propostaOuOs: string | null;

  initialMovimentacaoId?: string | number | null;
  initialItemId?: string | number | null;
  initialProdutoId?: string | number | null;

  rawMovs: StandbyMovAPI[];
  refresh?: () => Promise<void> | void;
  onClose: () => void;
  title?: string;
};

type FlatComercialItem = {
  key: string;
  mov: StandbyMovAPI["movimentacao"];
  item: StandbyItemAPI;
  index: number;
};

type OsInfoItem = {
  label: string;
  value: string;
};

type ApiResponse = {
  ok?: boolean;
  erro?: string;
  erros?: string[];
  [key: string]: unknown;
};

const API_BASE = "https://api.eva-googlemarine.com";

const URL_RETORNAR = `${API_BASE}/movimentar-vendas-retornar`;

const URL_BAIXAR = `${API_BASE}/movimentar-vendas-baixar`;

const ACCESS_TOKEN_KEYS = ["accessToken", "eva:accessToken", "access_token"];

const HEADER_HEIGHT = 52;
const LEFTBAR_WIDTH = 64;
const MOBILE_BREAKPOINT = 900;

const COMERCIAL_STANDBY_SIDE_PANEL_CSS = `
.comercial-standby-backdrop {
  position: fixed;
  top: ${HEADER_HEIGHT}px;
  left: ${LEFTBAR_WIDTH}px;
  right: 0;
  bottom: 0;
  z-index: 1190;
  display: flex;
  justify-content: flex-end;
  background:
    radial-gradient(
      circle at top left,
      rgba(47, 63, 89, 0.10),
      transparent 34%
    ),
    rgba(15, 23, 42, 0.28);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  box-sizing: border-box;
}

.comercial-standby-panel {
  width: min(
    1180px,
    calc(100vw - ${LEFTBAR_WIDTH}px)
  );
  height: calc(
    100vh - ${HEADER_HEIGHT}px
  );
  background: #ffffff;
  border-left: 1px solid #d9e2ec;
  box-shadow:
    -18px 0 46px
    rgba(15, 23, 42, 0.22);
  display: flex;
  flex-direction: column;
  font-family:
    Nunito,
    Inter,
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  color: #0f172a;
}

.comercial-standby-header {
  padding: 18px 20px 16px;
  border-bottom: 1px solid #e5eaf0;
  background:
    linear-gradient(
      135deg,
      rgba(248, 250, 252, 0.98),
      #ffffff
    );
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.comercial-standby-header-text {
  min-width: 0;
}

.comercial-standby-eyebrow {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.42px;
  text-transform: uppercase;
}

.comercial-standby-title {
  margin: 6px 0 4px;
  color: #0f172a;
  font-size: 21px;
  font-weight: 950;
  line-height: 1.18;
  letter-spacing: -0.02em;
  overflow-wrap: anywhere;
}

.comercial-standby-subtitle {
  margin: 0;
  max-width: 780px;
  color: #64748b;
  font-size: 12.5px;
  font-weight: 760;
  line-height: 1.45;
}

.comercial-standby-close-button {
  width: 36px;
  height: 36px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #334155;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    color 0.16s ease,
    transform 0.16s ease;
}

.comercial-standby-close-button:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateY(-1px);
}

.comercial-standby-content {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background:
    linear-gradient(
      180deg,
      #f8fafc 0%,
      #f1f5f9 100%
    );
  display: grid;
  grid-template-columns:
    minmax(0, 1fr)
    340px;
  gap: 12px;
  padding: 14px;
}

.comercial-standby-main {
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.comercial-standby-aside {
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  box-shadow:
    0 1px 0
    rgba(15, 23, 42, 0.03);
}

.comercial-standby-aside-header {
  padding: 13px;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
}

.comercial-standby-aside-header strong {
  display: block;
  color: #0f172a;
  font-size: 13.5px;
  font-weight: 950;
}

.comercial-standby-aside-header span {
  display: block;
  margin-top: 3px;
  color: #64748b;
  font-size: 11.5px;
  font-weight: 760;
}

.comercial-standby-item-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.comercial-standby-item-card {
  border: 0;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
  text-align: left;
  padding: 11px 12px;
  cursor: pointer;
  font-family: inherit;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.comercial-standby-item-card:hover {
  background: #f8fafc;
}

.comercial-standby-item-card.is-active {
  background: #eef5ff;
  box-shadow:
    inset 4px 0 0 #2f3f59;
}

.comercial-standby-item-code {
  color: #0f172a;
  font-size: 12.5px;
  font-weight: 950;
  overflow-wrap: anywhere;
}

.comercial-standby-item-name {
  color: #475569;
  font-size: 12px;
  font-weight: 780;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.comercial-standby-item-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.comercial-standby-chip {
  min-height: 22px;
  border: 1px solid #dbe4ef;
  background: #f8fafc;
  color: #475569;
  padding: 0 7px;
  display: inline-flex;
  align-items: center;
  font-size: 10.5px;
  font-weight: 900;
}

.comercial-standby-chip.is-danger {
  background: #fff5f5;
  color: #991b1b;
  border-color: #fecaca;
}

.comercial-standby-chip.is-blue {
  background: #eff6ff;
  color: #1d4ed8;
  border-color: #bfdbfe;
}

.comercial-standby-chip.is-green {
  background: #ecfdf3;
  color: #166534;
  border-color: #bbf7d0;
}

.comercial-standby-section {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  padding: 13px;
  display: flex;
  flex-direction: column;
  gap: 11px;
  box-shadow:
    0 1px 0
    rgba(15, 23, 42, 0.03);
}

.comercial-standby-section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.comercial-standby-section-title {
  min-width: 0;
}

.comercial-standby-section-title strong {
  display: block;
  color: #0f172a;
  font-size: 13.5px;
  font-weight: 950;
}

.comercial-standby-section-title span {
  display: block;
  margin-top: 3px;
  color: #64748b;
  font-size: 12px;
  font-weight: 760;
  line-height: 1.35;
}

.comercial-standby-info-grid,
.comercial-standby-summary-grid,
.comercial-standby-action-grid,
.comercial-standby-product-metrics {
  display: grid;
  gap: 8px;
}

.comercial-standby-info-grid,
.comercial-standby-summary-grid,
.comercial-standby-action-grid {
  grid-template-columns:
    repeat(
      4,
      minmax(0, 1fr)
    );
}

.comercial-standby-product-metrics {
  grid-template-columns:
    repeat(
      2,
      minmax(0, 1fr)
    );
}

.comercial-standby-info-box,
.comercial-standby-product-metric {
  border: 1px solid #edf2f7;
  background: #f8fafc;
  padding: 9px 10px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.comercial-standby-info-label,
.comercial-standby-product-metric span {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
}

.comercial-standby-info-value,
.comercial-standby-product-metric strong {
  color: #0f172a;
  font-size: 12.5px;
  font-weight: 900;
  overflow-wrap: anywhere;
}

.comercial-standby-summary-card {
  border: 1px solid #e2e8f0;
  background:
    linear-gradient(
      180deg,
      #ffffff 0%,
      #fbfdff 100%
    );
  padding: 13px 13px 12px;
  min-height: 86px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.comercial-standby-summary-card span {
  font-size: 11px;
  font-weight: 900;
  color: #64748b;
}

.comercial-standby-summary-card strong {
  font-size: 18px;
  line-height: 1.15;
  color: #0f172a;
  font-weight: 950;
  overflow-wrap: anywhere;
}

.comercial-standby-summary-card small {
  font-size: 11px;
  color: #64748b;
  font-weight: 760;
  line-height: 1.25;
}

.comercial-standby-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.comercial-standby-field.is-wide {
  grid-column: span 4;
}

.comercial-standby-field label {
  color: #334155;
  font-size: 11.5px;
  font-weight: 900;
}

.comercial-standby-field select,
.comercial-standby-field input,
.comercial-standby-field textarea {
  width: 100%;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #0f172a;
  font-family: inherit;
  font-size: 13px;
  font-weight: 760;
  outline: none;
  box-sizing: border-box;
}

.comercial-standby-field select,
.comercial-standby-field input {
  height: 36px;
  padding: 0 10px;
}

.comercial-standby-field textarea {
  min-height: 70px;
  resize: vertical;
  padding: 9px 10px;
}

.comercial-standby-field select:disabled,
.comercial-standby-field input:disabled,
.comercial-standby-field textarea:disabled {
  background: #f8fafc;
  color: #94a3b8;
  cursor: not-allowed;
}

.comercial-standby-actions-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.comercial-standby-primary-button,
.comercial-standby-secondary-button,
.comercial-standby-danger-button {
  height: 36px;
  border: 1px solid #cbd5e1;
  padding: 0 13px;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    transform 0.16s ease,
    opacity 0.16s ease;
}

.comercial-standby-primary-button {
  border-color: #2f3f59;
  background: #2f3f59;
  color: #ffffff;
}

.comercial-standby-secondary-button {
  background: #ffffff;
  color: #334155;
}

.comercial-standby-danger-button {
  border-color: #b91c1c;
  background: #fff5f5;
  color: #991b1b;
}

.comercial-standby-primary-button:hover:not(:disabled) {
  background: #243247;
  transform: translateY(-1px);
}

.comercial-standby-secondary-button:hover:not(:disabled) {
  background: #f8fafc;
  transform: translateY(-1px);
}

.comercial-standby-danger-button:hover:not(:disabled) {
  background: #fee2e2;
  transform: translateY(-1px);
}

.comercial-standby-primary-button:disabled,
.comercial-standby-secondary-button:disabled,
.comercial-standby-danger-button:disabled,
.comercial-standby-close-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}

.comercial-standby-selected-product {
  display: grid;
  grid-template-columns:
    minmax(0, 1fr)
    170px;
  gap: 10px;
}

.comercial-standby-product-card {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.comercial-standby-product-title {
  color: #0f172a;
  font-size: 15px;
  font-weight: 950;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.comercial-standby-product-subtitle {
  color: #64748b;
  font-size: 12px;
  font-weight: 760;
  line-height: 1.35;
}

.comercial-standby-alert {
  border: 1px solid #dbe4ef;
  background: #ffffff;
  padding: 12px;
  color: #475569;
  font-size: 12.5px;
  font-weight: 850;
}

.comercial-standby-alert.is-error {
  border-color: #fecaca;
  background: #fff5f5;
  color: #991b1b;
}

.comercial-standby-alert.is-success {
  border-color: #bbf7d0;
  background: #f0fdf4;
  color: #166534;
}

.comercial-standby-empty {
  padding: 16px;
  border: 1px dashed #cbd5e1;
  background: #ffffff;
  color: #64748b;
  font-size: 12.5px;
  font-weight: 850;
}

@media (
  max-width:
  ${MOBILE_BREAKPOINT}px
) {
  .comercial-standby-backdrop {
    top: 0;
    left: 0;
    z-index: 1400;
  }

  .comercial-standby-panel {
    width: 100vw;
    height: 100vh;
  }

  .comercial-standby-content {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }

  .comercial-standby-main {
    overflow: visible;
  }

  .comercial-standby-aside {
    min-height: 360px;
  }

  .comercial-standby-info-grid,
  .comercial-standby-summary-grid,
  .comercial-standby-action-grid,
  .comercial-standby-product-metrics {
    grid-template-columns:
      repeat(
        2,
        minmax(0, 1fr)
      );
  }

  .comercial-standby-field.is-wide {
    grid-column: span 2;
  }

  .comercial-standby-selected-product {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .comercial-standby-header {
    padding: 14px;
  }

  .comercial-standby-title {
    font-size: 17px;
  }

  .comercial-standby-content {
    padding: 10px;
  }

  .comercial-standby-info-grid,
  .comercial-standby-summary-grid,
  .comercial-standby-action-grid,
  .comercial-standby-product-metrics {
    grid-template-columns: 1fr;
  }

  .comercial-standby-field.is-wide {
    grid-column: span 1;
  }

  .comercial-standby-actions-row {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .comercial-standby-primary-button,
  .comercial-standby-secondary-button,
  .comercial-standby-danger-button {
    width: 100%;
  }
}
`;

function getStoredAccessToken(): string {
  try {
    for (const key of ACCESS_TOKEN_KEYS) {
      const value = localStorage.getItem(key);

      if (value?.trim()) {
        return value.trim();
      }
    }
  } catch {
    // localStorage indisponível.
  }

  return "";
}

function buildAuthHeaders(includeContentType = false): Record<string, string> {
  const token = getStoredAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

async function readJsonSafe<T>(res: Response): Promise<T | null> {
  const text = await res.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

async function postJSON<T extends ApiResponse = ApiResponse>(
  url: string,
  payload: unknown
): Promise<T | null> {
  const res = await fetch(url, {
    method: "POST",
    headers: buildAuthHeaders(true),
    cache: "no-store",
    body: JSON.stringify(payload),
  });

  const json = await readJsonSafe<T>(res);

  if (!res.ok || json?.ok === false) {
    const message =
      json?.erro ||
      (Array.isArray(json?.erros) ? json!.erros.join(" | ") : "") ||
      `HTTP ${res.status}`;

    throw new Error(message);
  }

  return json;
}

function normKey(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");
}

function getPropostaOuOs(mov: StandbyMovAPI["movimentacao"]): string {
  const proposta = String(mov.proposta_venda ?? "").trim();

  const os = String(mov.os_numero ?? "").trim();

  if (proposta && proposta.toLowerCase() !== "n/a") {
    return proposta;
  }

  if (os && os.toLowerCase() !== "n/a") {
    return os;
  }

  return "-";
}

function toNumberSafe(value: unknown): number {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const normalized = String(value)
    .trim()
    .replace(/[^\d,.-]/g, "")
    .replace(/\.(?=\d{3}(?:\D|$))/g, "")
    .replace(",", ".");

  const number = Number(normalized);

  return Number.isFinite(number) ? number : 0;
}

function formatQuantidade(value: unknown): string {
  return new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: 4,
  }).format(toNumberSafe(value));
}

function formatCurrency(value: unknown, moeda?: string | null): string {
  const number = toNumberSafe(value);

  const currency = String(moeda || "BRL")
    .trim()
    .toUpperCase();

  if (currency === "BRL") {
    return number.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  }

  const symbol = currency === "USD" ? "$" : currency === "EUR" ? "€" : currency;

  return `${symbol} ${number.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDateTime(iso?: string | null): string {
  if (!iso) {
    return "—";
  }

  try {
    const date = new Date(iso);

    if (Number.isNaN(date.getTime())) {
      return iso;
    }

    const data = date.toLocaleDateString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const hora = date.toLocaleTimeString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return `${data} - ${hora}`;
  } catch {
    return iso;
  }
}

function toStartOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseDateSmart(value: string): Date | null {
  if (!value) {
    return null;
  }

  const match = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(value.trim());

  if (match) {
    const date = new Date(
      Number(match[3]),
      Number(match[2]) - 1,
      Number(match[1])
    );

    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

function diffDaysFromToday(value: string): number | null {
  const date = parseDateSmart(value);

  if (!date) {
    return null;
  }

  const difference =
    toStartOfDay(new Date()).getTime() - toStartOfDay(date).getTime();

  return Math.max(0, Math.floor(difference / 86400000));
}

function getMovDate(movs: StandbyMovAPI[]): string {
  const first = movs[0]?.movimentacao?.criado_em;

  return first ? formatDateTime(first) : "—";
}

function getCliente(movs: StandbyMovAPI[]): string {
  const found = movs.find(
    ({ movimentacao }) =>
      String(movimentacao.cliente_nome || "").trim() ||
      String(movimentacao.empresa_cliente || "").trim()
  );

  return (
    String(
      found?.movimentacao.cliente_nome ||
        found?.movimentacao.empresa_cliente ||
        ""
    ).trim() || "—"
  );
}

function getEmbarcacao(movs: StandbyMovAPI[]): string {
  const found = movs.find(({ movimentacao }) =>
    String(movimentacao.embarcacao || "").trim()
  );

  return String(found?.movimentacao.embarcacao || "").trim() || "—";
}

function getResponsavelNome(movs: StandbyMovAPI[]): string {
  const found = movs.find(
    ({ movimentacao }) =>
      String(movimentacao.responsavel_nome || "").trim() ||
      String(movimentacao.coletor_nome || "").trim()
  );

  return (
    String(
      found?.movimentacao.responsavel_nome ||
        found?.movimentacao.coletor_nome ||
        ""
    ).trim() || "—"
  );
}

function getResponsavelId(movs: StandbyMovAPI[]): number | null {
  const found = movs.find(
    ({ movimentacao }) => movimentacao.responsavel_id !== null
  );

  const id = Number(found?.movimentacao.responsavel_id);

  return Number.isFinite(id) ? Math.trunc(id) : null;
}

function getTipoLabel(movs: StandbyMovAPI[]): string {
  const raw = String(
    movs[0]?.movimentacao.tipo_standby || movs[0]?.movimentacao.tipo || ""
  ).toLowerCase();

  if (raw.includes("venda")) {
    return "Venda";
  }

  if (raw.includes("servico") || raw.includes("serviço")) {
    return "Serviço";
  }

  return raw || "—";
}

function getMovCodigo(mov: StandbyMovAPI["movimentacao"]): string {
  return String(mov.codigo_movimentacao || mov.tipo || mov.tipo_standby || "—");
}

function flattenItems(movs: StandbyMovAPI[]): FlatComercialItem[] {
  const output: FlatComercialItem[] = [];

  movs.forEach((mov, movIndex) => {
    (mov.itens || []).forEach((item, itemIndex) => {
      output.push({
        key:
          `${mov.movimentacao.id}-` +
          `${item.id}-` +
          `${item.produto_id}-` +
          `${movIndex}-` +
          `${itemIndex}`,

        mov: mov.movimentacao,

        item,

        index: output.length,
      });
    });
  });

  return output;
}

function buildOsInfo(
  propostaOuOs: string,
  movs: StandbyMovAPI[],
  items: FlatComercialItem[]
): OsInfoItem[] {
  const totalUnidades = items.reduce(
    (acc, entry) => acc + toNumberSafe(entry.item.quantidade),
    0
  );

  const totalCusto = items.reduce((acc, entry) => {
    const quantidade = toNumberSafe(entry.item.quantidade);

    const custo = toNumberSafe(entry.item.custo_unitario);

    return acc + quantidade * custo;
  }, 0);

  const dataHora = getMovDate(movs);

  const diasAtraso = diffDaysFromToday(dataHora.split(" - ")[0] || "—");

  return [
    {
      label: "P.V. / OS",
      value: propostaOuOs || "—",
    },
    {
      label: "Tipo",
      value: getTipoLabel(movs),
    },
    {
      label: "Cliente",
      value: getCliente(movs),
    },
    {
      label: "Embarcação",
      value: getEmbarcacao(movs),
    },
    {
      label: "R. retirada",
      value: getResponsavelNome(movs),
    },
    {
      label: "Data / Hora",
      value: dataHora,
    },
    {
      label: "Dias de atraso",
      value: diasAtraso === null ? "—" : String(diasAtraso),
    },
    {
      label: "Materiais",
      value:
        `${items.length} item(ns), ` +
        `${formatQuantidade(totalUnidades)} unidade(s)`,
    },
    {
      label: "Custo total aprox.",
      value: totalCusto > 0 ? formatCurrency(totalCusto, "BRL") : "—",
    },
  ];
}

const InfoBox: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <div className="comercial-standby-info-box">
    <span className="comercial-standby-info-label">{label}</span>

    <strong className="comercial-standby-info-value">{value || "—"}</strong>
  </div>
);

const SummaryCard: React.FC<{
  label: string;
  value: React.ReactNode;
  helper?: string;
}> = ({ label, value, helper }) => (
  <div className="comercial-standby-summary-card">
    <span>{label}</span>

    <strong>{value}</strong>

    {helper && <small>{helper}</small>}
  </div>
);

const ProductMetric: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <div className="comercial-standby-product-metric">
    <span>{label}</span>

    <strong>{value || "—"}</strong>
  </div>
);

const ComercialStandbySidePanel: React.FC<ComercialStandbySidePanelProps> = ({
  open,
  propostaOuOs,

  initialMovimentacaoId,
  initialItemId,
  initialProdutoId,

  rawMovs,
  refresh,
  onClose,

  title = "Comercial - Standby",
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [action, setAction] = useState<ComercialStandbyAction>("baixar");

  const [scope, setScope] = useState<ComercialStandbyScope>("item");

  const [quantidade, setQuantidade] = useState("1");

  const [motivo, setMotivo] = useState("");

  const [busy, setBusy] = useState(false);

  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  const [feedbackSuccess, setFeedbackSuccess] = useState<string | null>(null);

  const chaveAberta = useMemo(() => normKey(propostaOuOs), [propostaOuOs]);

  const movsDaOs = useMemo(() => {
    if (!chaveAberta) {
      return [];
    }

    return (rawMovs || []).filter((mov) => {
      const chave = getPropostaOuOs(mov.movimentacao);

      return normKey(chave) === chaveAberta;
    });
  }, [rawMovs, chaveAberta]);

  const items = useMemo(() => flattenItems(movsDaOs), [movsDaOs]);

  const safeSelectedIndex =
    selectedIndex >= 0 && selectedIndex < items.length ? selectedIndex : 0;

  const selectedEntry = items[safeSelectedIndex] || null;

  const responsavelId = useMemo(() => getResponsavelId(movsDaOs), [movsDaOs]);

  const osInfo = useMemo(
    () => buildOsInfo(String(propostaOuOs || "—"), movsDaOs, items),
    [propostaOuOs, movsDaOs, items]
  );

  const totalUnidades = useMemo(
    () =>
      items.reduce(
        (acc, entry) => acc + toNumberSafe(entry.item.quantidade),
        0
      ),
    [items]
  );

  const totalCusto = useMemo(
    () =>
      items.reduce((acc, entry) => {
        const qtd = toNumberSafe(entry.item.quantidade);

        const custo = toNumberSafe(entry.item.custo_unitario);

        return acc + qtd * custo;
      }, 0),
    [items]
  );

  const selectedMax = toNumberSafe(selectedEntry?.item.quantidade);

  const selectedMoeda = selectedEntry?.item.moeda || "BRL";

  const selectedCusto = selectedEntry?.item.custo_unitario ?? null;

  const selectedSubtotal = selectedMax * toNumberSafe(selectedCusto);

  useEffect(() => {
    if (!open) {
      return;
    }

    setAction("baixar");
    setScope("item");
    setQuantidade("1");
    setMotivo("");
    setBusy(false);
    setFeedbackError(null);
    setFeedbackSuccess(null);
  }, [open, propostaOuOs]);

  /*
   * Seleciona exatamente o item
   * recebido do duplo clique.
   *
   * Prioridade:
   * 1. movimentação + item;
   * 2. movimentação + produto;
   * 3. primeiro item.
   */
  useEffect(() => {
    if (!open || items.length === 0) {
      return;
    }

    const movimentacaoAlvo = String(initialMovimentacaoId ?? "");

    const itemAlvo = String(initialItemId ?? "");

    const produtoAlvo = String(initialProdutoId ?? "");

    let targetIndex = items.findIndex(
      (entry) =>
        String(entry.mov.id) === movimentacaoAlvo &&
        String(entry.item.id) === itemAlvo
    );

    if (targetIndex < 0 && movimentacaoAlvo && produtoAlvo) {
      targetIndex = items.findIndex(
        (entry) =>
          String(entry.mov.id) === movimentacaoAlvo &&
          String(entry.item.produto_id) === produtoAlvo
      );
    }

    setSelectedIndex(targetIndex >= 0 ? targetIndex : 0);
  }, [open, items, initialMovimentacaoId, initialItemId, initialProdutoId]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, busy, onClose]);

  useEffect(() => {
    if (!selectedEntry) {
      setQuantidade("1");
      return;
    }

    const max = toNumberSafe(selectedEntry.item.quantidade);

    const atual = toNumberSafe(quantidade);

    if (!atual || atual > max) {
      setQuantidade(max > 0 ? "1" : "0");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntry?.key]);

  if (!open) {
    return null;
  }

  const canSubmit =
    !busy &&
    !!propostaOuOs &&
    movsDaOs.length > 0 &&
    items.length > 0 &&
    responsavelId !== null &&
    (scope === "os" || !!selectedEntry);

  function validateQuantidadeItem(): number | null {
    if (!selectedEntry) {
      setFeedbackError("Selecione um material na listagem lateral.");

      return null;
    }

    const max = selectedMax;

    const qtd = toNumberSafe(quantidade);

    if (!(max > 0)) {
      setFeedbackError("Item sem quantidade disponível para movimentação.");

      return null;
    }

    if (!Number.isInteger(qtd) || qtd <= 0) {
      setFeedbackError("Informe uma quantidade inteira maior que zero.");

      return null;
    }

    if (qtd > max) {
      setFeedbackError(
        `A quantidade não pode exceder ${formatQuantidade(max)}.`
      );

      return null;
    }

    return qtd;
  }

  function buildPayloadItem(qtd: number) {
    if (!selectedEntry) {
      throw new Error("Item selecionado não encontrado.");
    }

    /*
     * A API de retorno continua
     * recebendo movimentacao_id.
     *
     * A API de baixa recebe:
     * origem_mov_id + item_id.
     */
    const baseItem =
      action === "retornar"
        ? {
            movimentacao_id: selectedEntry.item.movimentacao_id,

            produto_id: selectedEntry.item.produto_id,

            quantidade: qtd,
          }
        : {
            origem_mov_id: selectedEntry.item.movimentacao_id,

            item_id: selectedEntry.item.id,

            produto_id: selectedEntry.item.produto_id,

            quantidade: qtd,
          };

    return {
      responsavel_id: responsavelId,

      proposta_venda: propostaOuOs,

      ...(motivo.trim()
        ? {
            motivo: motivo.trim(),
          }
        : {}),

      itens: [baseItem],
    };
  }

  function buildPayloadOs() {
    const itensValidos = items.filter(
      (entry) => toNumberSafe(entry.item.quantidade) > 0
    );

    if (!itensValidos.length) {
      throw new Error(
        "Não há itens com quantidade disponível para movimentação."
      );
    }

    return {
      responsavel_id: responsavelId,

      proposta_venda: propostaOuOs,

      ...(motivo.trim()
        ? {
            motivo: motivo.trim(),
          }
        : {}),

      itens: itensValidos.map((entry) =>
        action === "retornar"
          ? {
              movimentacao_id: entry.item.movimentacao_id,

              produto_id: entry.item.produto_id,

              quantidade: toNumberSafe(entry.item.quantidade),
            }
          : {
              origem_mov_id: entry.item.movimentacao_id,

              item_id: entry.item.id,

              produto_id: entry.item.produto_id,

              quantidade: toNumberSafe(entry.item.quantidade),
            }
      ),
    };
  }

  async function handleSubmit() {
    setFeedbackError(null);
    setFeedbackSuccess(null);

    if (!canSubmit) {
      setFeedbackError(
        responsavelId === null
          ? "Não foi possível identificar o responsável da P.V./OS."
          : "Dados da P.V./OS indisponíveis para movimentação."
      );

      return;
    }

    try {
      setBusy(true);

      let payload;

      if (scope === "item") {
        const qtd = validateQuantidadeItem();

        if (qtd === null) {
          return;
        }

        payload = buildPayloadItem(qtd);
      } else {
        payload = buildPayloadOs();
      }

      const url = action === "retornar" ? URL_RETORNAR : URL_BAIXAR;

      await postJSON(url, payload);

      setFeedbackSuccess(
        action === "retornar"
          ? scope === "item"
            ? "Retorno do item realizado com sucesso."
            : "Retorno da P.V./OS realizado com sucesso."
          : scope === "item"
          ? "Baixa do item realizada com sucesso."
          : "Baixa da P.V./OS realizada com sucesso."
      );

      await refresh?.();

      window.setTimeout(onClose, 650);
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "Falha ao executar movimentação.";

      setFeedbackError(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <style>{COMERCIAL_STANDBY_SIDE_PANEL_CSS}</style>

      <div
        className="comercial-standby-backdrop"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !busy) {
            onClose();
          }
        }}
      >
        <aside
          className="comercial-standby-panel"
          onMouseDown={(event) => event.stopPropagation()}
          aria-label="Detalhamento do standby comercial"
        >
          <div className="comercial-standby-header">
            <div className="comercial-standby-header-text">
              <div className="comercial-standby-eyebrow">
                {title}
                {" / Detalhamento comercial"}
              </div>

              <h2 className="comercial-standby-title">
                {propostaOuOs || "P.V./OS não informada"}
              </h2>

              <p className="comercial-standby-subtitle">
                Consulta da P.V./OS, materiais em standby, responsável pela
                retirada e ações de baixa ou retorno.
              </p>
            </div>

            <button
              type="button"
              className="comercial-standby-close-button"
              onClick={onClose}
              disabled={busy}
              aria-label="Fechar detalhamento comercial"
            >
              ×
            </button>
          </div>

          <div className="comercial-standby-content">
            <main className="comercial-standby-main">
              {movsDaOs.length === 0 ? (
                <div className="comercial-standby-empty">
                  Nenhum dado localizado para esta P.V./OS.
                </div>
              ) : (
                <>
                  <section className="comercial-standby-summary-grid">
                    <SummaryCard
                      label="Materiais"
                      value={items.length}
                      helper="Itens diferentes em standby"
                    />

                    <SummaryCard
                      label="Unidades"
                      value={formatQuantidade(totalUnidades)}
                      helper="Quantidade total pendente"
                    />

                    <SummaryCard
                      label="Custo total aprox."
                      value={totalCusto > 0 ? formatCurrency(totalCusto) : "—"}
                      helper="Custo unitário x quantidade"
                    />

                    <SummaryCard
                      label="Responsável"
                      value={getResponsavelNome(movsDaOs)}
                      helper={
                        responsavelId !== null
                          ? `ID ${responsavelId}`
                          : "ID não localizado"
                      }
                    />
                  </section>

                  <section className="comercial-standby-section">
                    <div className="comercial-standby-section-header">
                      <div className="comercial-standby-section-title">
                        <strong>Informações da P.V. / OS</strong>

                        <span>
                          Dados operacionais vinculados à saída comercial em
                          standby.
                        </span>
                      </div>
                    </div>

                    <div className="comercial-standby-info-grid">
                      {osInfo.map((item) => (
                        <InfoBox
                          key={`${item.label}-` + `${item.value}`}
                          label={item.label}
                          value={item.value}
                        />
                      ))}
                    </div>
                  </section>

                  <section className="comercial-standby-section">
                    <div className="comercial-standby-section-header">
                      <div className="comercial-standby-section-title">
                        <strong>Ação comercial</strong>

                        <span>
                          Selecione se a movimentação será feita em um item ou
                          na P.V./OS inteira.
                        </span>
                      </div>
                    </div>

                    <div className="comercial-standby-action-grid">
                      <div className="comercial-standby-field">
                        <label htmlFor="comercial-standby-action">Ação</label>

                        <select
                          id="comercial-standby-action"
                          value={action}
                          disabled={busy}
                          onChange={(event) =>
                            setAction(
                              event.target.value as ComercialStandbyAction
                            )
                          }
                        >
                          <option value="baixar">Dar baixa</option>

                          <option value="retornar">Retornar ao estoque</option>
                        </select>
                      </div>

                      <div className="comercial-standby-field">
                        <label htmlFor="comercial-standby-scope">
                          Aplicar em
                        </label>

                        <select
                          id="comercial-standby-scope"
                          value={scope}
                          disabled={busy}
                          onChange={(event) =>
                            setScope(
                              event.target.value as ComercialStandbyScope
                            )
                          }
                        >
                          <option value="item">Item selecionado</option>

                          <option value="os">P.V./OS inteira</option>
                        </select>
                      </div>

                      <div className="comercial-standby-field">
                        <label htmlFor="comercial-standby-quantity">
                          Quantidade
                        </label>

                        <input
                          id="comercial-standby-quantity"
                          type="text"
                          inputMode="numeric"
                          value={scope === "os" ? "Todos os itens" : quantidade}
                          disabled={busy || scope === "os"}
                          onChange={(event) =>
                            setQuantidade(event.target.value)
                          }
                        />
                      </div>

                      <div className="comercial-standby-field">
                        <label htmlFor="comercial-standby-max">
                          Máximo do item
                        </label>

                        <input
                          id="comercial-standby-max"
                          type="text"
                          value={
                            selectedEntry
                              ? formatQuantidade(selectedEntry.item.quantidade)
                              : "—"
                          }
                          disabled
                          readOnly
                        />
                      </div>

                      <div className="comercial-standby-field is-wide">
                        <label htmlFor="comercial-standby-reason">
                          Observação / motivo
                        </label>

                        <textarea
                          id="comercial-standby-reason"
                          value={motivo}
                          disabled={busy}
                          placeholder="Opcional. Ex.: baixa definitiva por venda concluída, retorno parcial, ajuste operacional..."
                          onChange={(event) => setMotivo(event.target.value)}
                        />
                      </div>
                    </div>

                    {feedbackError && (
                      <div className="comercial-standby-alert is-error">
                        {feedbackError}
                      </div>
                    )}

                    {feedbackSuccess && (
                      <div className="comercial-standby-alert is-success">
                        {feedbackSuccess}
                      </div>
                    )}

                    <div className="comercial-standby-actions-row">
                      <button
                        type="button"
                        className="comercial-standby-secondary-button"
                        onClick={onClose}
                        disabled={busy}
                      >
                        Cancelar
                      </button>

                      <button
                        type="button"
                        className={
                          action === "baixar"
                            ? "comercial-standby-danger-button"
                            : "comercial-standby-primary-button"
                        }
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                      >
                        {busy
                          ? "Processando…"
                          : action === "baixar"
                          ? scope === "item"
                            ? "Dar baixa no item"
                            : "Dar baixa na P.V./OS"
                          : scope === "item"
                          ? "Retornar item"
                          : "Retornar P.V./OS"}
                      </button>
                    </div>
                  </section>

                  <section className="comercial-standby-section">
                    <div className="comercial-standby-section-header">
                      <div className="comercial-standby-section-title">
                        <strong>Material selecionado</strong>

                        <span>
                          Detalhamento do item escolhido na listagem lateral.
                        </span>
                      </div>
                    </div>

                    {selectedEntry ? (
                      <div className="comercial-standby-selected-product">
                        <div className="comercial-standby-product-card">
                          <div>
                            <div className="comercial-standby-product-title">
                              {selectedEntry.item.produto.codigo}
                              {" · "}
                              {selectedEntry.item.produto.nome}
                            </div>

                            <div className="comercial-standby-product-subtitle">
                              Movimento {selectedEntry.mov.id}
                              {" · "}
                              {getMovCodigo(selectedEntry.mov)}
                            </div>
                          </div>

                          <div className="comercial-standby-product-metrics">
                            <ProductMetric
                              label="Quantidade"
                              value={formatQuantidade(
                                selectedEntry.item.quantidade
                              )}
                            />

                            <ProductMetric
                              label="Custo unitário"
                              value={formatCurrency(
                                selectedEntry.item.custo_unitario,
                                selectedMoeda
                              )}
                            />

                            <ProductMetric
                              label="Subtotal aprox."
                              value={formatCurrency(
                                selectedSubtotal,
                                selectedMoeda
                              )}
                            />

                            <ProductMetric
                              label="Moeda"
                              value={selectedMoeda}
                            />

                            <ProductMetric
                              label="NCM"
                              value={selectedEntry.item.produto.ncm || "—"}
                            />

                            <ProductMetric
                              label="Classificação"
                              value={
                                selectedEntry.item.produto.classificacao || "—"
                              }
                            />

                            <ProductMetric
                              label="Posição"
                              value={selectedEntry.item.produto.posicao || "—"}
                            />

                            <ProductMetric
                              label="Data da retirada"
                              value={formatDateTime(
                                selectedEntry.mov.criado_em
                              )}
                            />
                          </div>
                        </div>

                        <div className="comercial-standby-product-card">
                          <div className="comercial-standby-product-title">
                            Identificadores
                          </div>

                          <div className="comercial-standby-product-metrics">
                            <ProductMetric
                              label="Item ID"
                              value={selectedEntry.item.id}
                            />

                            <ProductMetric
                              label="Produto ID"
                              value={selectedEntry.item.produto_id}
                            />

                            <ProductMetric
                              label="Movimentação ID"
                              value={selectedEntry.item.movimentacao_id}
                            />

                            <ProductMetric
                              label="Estoque ID"
                              value={selectedEntry.item.estoque_id || "—"}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="comercial-standby-empty">
                        Selecione um material na listagem lateral.
                      </div>
                    )}
                  </section>
                </>
              )}
            </main>

            <aside className="comercial-standby-aside">
              <div className="comercial-standby-aside-header">
                <strong>Materiais da P.V./OS</strong>

                <span>
                  Clique em um item para aplicar baixa ou retorno individual.
                </span>
              </div>

              <div className="comercial-standby-item-list">
                {items.length === 0 && (
                  <div className="comercial-standby-empty">
                    Nenhum material disponível.
                  </div>
                )}

                {items.map((entry, index) => {
                  const active = index === safeSelectedIndex;

                  const qtd = toNumberSafe(entry.item.quantidade);

                  const custo = toNumberSafe(entry.item.custo_unitario);

                  const subtotal = qtd * custo;

                  const moeda = entry.item.moeda || "BRL";

                  return (
                    <button
                      key={entry.key}
                      type="button"
                      className={
                        active
                          ? "comercial-standby-item-card is-active"
                          : "comercial-standby-item-card"
                      }
                      aria-pressed={active}
                      onClick={() => {
                        setSelectedIndex(index);

                        setScope("item");

                        setQuantidade(qtd > 0 ? "1" : "0");

                        setFeedbackError(null);

                        setFeedbackSuccess(null);
                      }}
                    >
                      <div className="comercial-standby-item-code">
                        {entry.item.produto.codigo || "—"}
                      </div>

                      <div className="comercial-standby-item-name">
                        {entry.item.produto.nome || "Produto sem nome"}
                      </div>

                      <div className="comercial-standby-item-meta">
                        <span className="comercial-standby-chip is-blue">
                          Qtd. {formatQuantidade(qtd)}
                        </span>

                        <span className="comercial-standby-chip">
                          {formatCurrency(custo, moeda)}
                        </span>

                        <span className="comercial-standby-chip is-green">
                          {formatCurrency(subtotal, moeda)}
                        </span>

                        {entry.item.pendente_retorno && (
                          <span className="comercial-standby-chip is-danger">
                            Pendente
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </aside>
          </div>
        </aside>
      </div>
    </>
  );
};

export default ComercialStandbySidePanel;
